// Gewicht und Schwerpunkt — reine Funktionen, engine-frei (keine three.js-Typen).
//
// Praxisnutzen: Passt der Korpus ins Auto? Tragen das zwei Leute? Wie stark
// muss die Wandbefestigung sein? Kippt das Möbel nach vorn, wenn oben etwas
// Schweres hängt?
//
// Genauigkeit: gerechnet wird mit dem umschließenden Quader (L × B × S).
// Ausschnitte, Bohrungen, Nuten und Gehrungen bleiben bewusst unberücksichtigt
// — ihr Anteil liegt weit unter der Streuung der Rohdichte selbst. Für
// Transport- und Befestigungsfragen ist das genau genug.
// Teile ohne gepflegte Rohdichte fließen NICHT ein und werden gezählt, damit
// keine falsche Summe entsteht.
//
// AUSGEBLENDETE TEILE zählen mit — genau wie in Stückliste und Zuschnittplan
// (vgl. visibility.js): gebaut und getragen wird, was im Projekt steht, nicht
// was gerade zu sehen ist. Beim Arbeiten mit isolierten Baugruppen sieht die
// Summe dann größer aus als das Sichtbare; deshalb weist weightSummary() die
// ausgeblendeten Teile offen aus, statt einen zweiten Rechenmodus anzubieten.
// Ein Umschalter „nur sichtbare“ hieße: dieselbe Zahl bedeutet je nach
// Schalterstellung etwas anderes — und der Schalter steht garantiert falsch,
// wenn man ihn braucht.
import { partAabb } from "./snapping"
import { isHidden } from "./visibility"

// Alles, was bodennah steht, bildet die Standfläche (Sockel, Füße, Boden).
const FLOOR_TOLERANCE_MM = 30

// Material-Index darf eine Map (doc.materials) oder ein einfaches Objekt sein
function materialFor(materialsById, materialId) {
  if (!materialsById || materialId === null || materialId === undefined) return null
  const found = typeof materialsById.get === "function"
    ? materialsById.get(materialId)
    : materialsById[materialId]
  return found ?? null
}

function densityOf(material) {
  const density = Number(material?.density_kg_m3)
  return Number.isFinite(density) && density > 0 ? density : null
}

// Gewicht eines Teils in kg — null, wenn die Rohdichte fehlt (unbekannt ≠ 0).
// Rotation und Position spielen keine Rolle: Drehen macht nicht schwerer.
export function partWeight(part, material) {
  const density = densityOf(material)
  if (!density) return null
  const volumeM3 = (part.length_mm * part.width_mm * part.thickness_mm) / 1_000_000_000
  return volumeM3 * density
}

// { kg, knownCount, unknownCount } über alle übergebenen Teile
export function totalWeight(parts, materialsById) {
  let kg = 0
  let knownCount = 0
  let unknownCount = 0
  for (const part of parts) {
    const weight = partWeight(part, materialFor(materialsById, part.material_id))
    if (weight === null) unknownCount += 1
    else { kg += weight; knownCount += 1 }
  }
  return { kg, knownCount, unknownCount }
}

// Gewichteter Mittelpunkt der Teil-Mittelpunkte (Quader sind homogen, ihr
// Schwerpunkt liegt in der Mitte) → { x, y, z, kg, knownCount, unknownCount }.
// null, wenn kein einziges Teil eine Rohdichte hat.
export function centerOfMass(parts, materialsById) {
  let kg = 0
  let knownCount = 0
  let unknownCount = 0
  const sum = { x: 0, y: 0, z: 0 }
  for (const part of parts) {
    const weight = partWeight(part, materialFor(materialsById, part.material_id))
    if (weight === null) { unknownCount += 1; continue }
    kg += weight
    knownCount += 1
    sum.x += part.pos_x * weight
    sum.y += part.pos_y * weight
    sum.z += part.pos_z * weight
  }
  if (kg <= 0) return null
  return { x: sum.x / kg, y: sum.y / kg, z: sum.z / kg, kg, knownCount, unknownCount }
}

// Standfläche: Welt-AABB in x/z aller bodennahen Teile (unterster Punkt
// innerhalb FLOOR_TOLERANCE_MM über dem tiefsten Punkt des Möbels).
export function supportFootprint(parts, toleranceMm = FLOOR_TOLERANCE_MM) {
  if (parts.length === 0) return null
  const boxes = parts.map((part) => partAabb(part))
  const floorY = Math.min(...boxes.map((box) => box.min[1]))
  const standing = boxes.filter((box) => box.min[1] <= floorY + toleranceMm)
  return {
    minX: Math.min(...standing.map((box) => box.min[0])),
    maxX: Math.max(...standing.map((box) => box.max[0])),
    minZ: Math.min(...standing.map((box) => box.min[2])),
    maxZ: Math.max(...standing.map((box) => box.max[2]))
  }
}

// Kippgefahr: liegt das Lot des Schwerpunkts außerhalb der Standfläche, steht
// das Möbel nicht sicher. marginMm = Abstand zur nächsten Kante der
// Standfläche (negativ = außerhalb). Absichtlich grob: eine Warnung, keine
// Standsicherheitsberechnung — Verankerung in der Wand bleibt Sache des
// Schreiners.
export function tippingRisk(parts, materialsById, toleranceMm = FLOOR_TOLERANCE_MM) {
  const com = centerOfMass(parts, materialsById)
  if (!com) return null
  const footprint = supportFootprint(parts, toleranceMm)
  if (!footprint) return null
  const marginMm = Math.min(
    com.x - footprint.minX, footprint.maxX - com.x,
    com.z - footprint.minZ, footprint.maxZ - com.z
  )
  return { stable: marginMm >= 0, marginMm, com, footprint }
}

// Anzeigetext fürs Panel: unter 1 kg in Gramm, sonst auf 0,1 kg gerundet
export function formatWeight(kg) {
  if (kg < 1) return `${Math.round(kg * 1000)} g`
  return `${kg.toFixed(kg < 10 ? 2 : 1).replace(".", ",")} kg`
}

export function hiddenCount(parts) {
  return parts.filter((part) => isHidden(part)).length
}

// Vollständige Zeile fürs Teile-Panel, z. B.
//   „Gewicht 24,3 kg · 2 ohne Rohdichte · 3 Teile ausgeblendet mitgerechnet“
// Beide Zusätze erscheinen nur, wenn sie zutreffen. Leeres Projekt → "".
export function weightSummary(parts, materialsById) {
  if (parts.length === 0) return ""
  const { kg, knownCount, unknownCount } = totalWeight(parts, materialsById)
  const notes = []
  if (unknownCount > 0) notes.push(`${unknownCount} ohne Rohdichte`)
  const hidden = hiddenCount(parts)
  if (hidden > 0) {
    notes.push(`${hidden} ${hidden === 1 ? "Teil" : "Teile"} ausgeblendet mitgerechnet`)
  }
  return [ `Gewicht ${knownCount > 0 ? formatWeight(kg) : "—"}`, ...notes ].join(" · ")
}
